import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Check, ChevronRight, Compass, Leaf, Map, Menu, MessageCircle, Move, Palette, Pause, Play, Share2, Sparkles, Sun, Users, X } from "lucide-react";
import { initializePaddle } from "@paddle/paddle-js";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError } from "./lib/api";
import { inAppBrowser, startTracking, track, trackPageView } from "./lib/analytics";
import { loadTikTokPixel, tiktokIdentify, tiktokTrack } from "./lib/tiktok";
import { assignQuest } from "./lib/quests";
import { PROGRAM } from "./lib/program";
import { captureAttribution, getSession, updateSession } from "./lib/session";
import type { Distraction, LifeCategory, MokiSession, PublicConfig, QuestFeedback, RiskWindow } from "./lib/types";

const DEFAULT_CONFIG: PublicConfig = {
  foundingEnabled: false,
  foundingCap: 100,
  foundingRemaining: null,
  paddleMode: "sandbox",
  quizQuestion3Enabled: false,
  sevenDayEnabled: true,
  supportEmail: "hello@getnoki.app",
};

function usePublicConfig(): PublicConfig {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  useEffect(() => {
    let active = true;
    void api<PublicConfig>("/api/config").then((next) => { if (!active) return; setConfig(next); loadTikTokPixel(next.tiktokPixelId); }).catch(() => undefined);
    return () => { active = false; };
  }, []);
  return config;
}

function useSession(): MokiSession {
  const [session, setSession] = useState(getSession);
  useEffect(() => {
    const sync = () => setSession(getSession());
    window.addEventListener("moki-session", sync);
    return () => window.removeEventListener("moki-session", sync);
  }, []);
  return session;
}

function Brand({ light = false }: { light?: boolean }) {
  return (
    <Link to="/" className={`brand ${light ? "brand--light" : ""}`} aria-label="NOKI home">
      <img src="/assets/moki-mark.svg" alt="" width="36" height="36" />
      <span>NOKI</span>
    </Link>
  );
}

function Header({ compact = false }: { compact?: boolean }) {
  return (
    <header className={`site-header ${compact ? "site-header--compact" : ""}`}>
      <div className="shell site-header__inner">
        <Brand />
        <Link className="header-link" to="/founding" data-track="header_founding">Founding Pass <ChevronRight size={16} /></Link>
      </div>
    </header>
  );
}

function Footer() {
  const config = usePublicConfig();
  return (
    <footer className="footer">
      <div className="shell footer__inner">
        <Brand light />
        <p>Technology for putting the technology down.</p>
        <nav aria-label="Footer">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/refund">Refunds</Link>
          <a href={`mailto:${config.supportEmail}`} data-track="footer_contact">Contact</a>
        </nav>
      </div>
    </footer>
  );
}

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="form-error" role="alert">{message}</p>;
}

interface WaitlistResponse {
  userId: string;
  referralCode: string;
  alreadyJoined: boolean;
}

function WaitlistForm({ onSuccess, buttonLabel = "Join free", place = "modal" }: { onSuccess?: () => void; buttonLabel?: string; place?: string }) {
  const session = useSession();
  const [email, setEmail] = useState(session.email ?? "");
  const [firstName, setFirstName] = useState(session.firstName ?? "");
  const [status, setStatus] = useState<"idle" | "loading" | "done">(session.userId ? "done" : "idle");
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "loading") return;
    setError("");
    setStatus("loading");
    track("waitlist_start", { place });
    const current = getSession();
    const eventId = crypto.randomUUID();
    try {
      const result = await api<WaitlistResponse>("/api/waitlist", {
        method: "POST",
        body: {
          email,
          first_name: firstName || null,
          anonymous_id: session.anonymousId,
          attribution: captureAttribution(),
          primary_distraction: current.primaryDistraction ?? null,
          desired_life_category: current.desiredLifeCategory ?? null,
          in_app: inAppBrowser() || null,
          event_id: eventId,
        },
      });
      updateSession({ email: email.trim().toLowerCase(), firstName: firstName.trim() || undefined, userId: result.userId, referralCode: result.referralCode });
      setStatus("done");
      track("waitlist_complete", { already_joined: result.alreadyJoined, place, with_name: Boolean(firstName.trim()) });
      if (!result.alreadyJoined) {
        void tiktokIdentify(email, result.userId);
        tiktokTrack("CompleteRegistration", eventId);
      }
      onSuccess?.();
    } catch (cause) {
      setStatus("idle");
      const message = cause instanceof ApiError ? cause.message : "Could not join right now. Please try again.";
      setError(message);
      track("api_error", { step: "waitlist", code: cause instanceof ApiError ? cause.code ?? String(cause.status) : "unknown", message });
    }
  }

  if (status === "done" && !onSuccess) {
    return <div className="success-note"><Check size={18} /> You’re in. Your first Side Quest is ready.</div>;
  }

  return (
    <form className="waitlist-form" onSubmit={submit} noValidate>
      <label>
        <span>First name <small>optional</small></span>
        <input autoComplete="given-name" value={firstName} onChange={(event) => setFirstName(event.target.value)} maxLength={80} placeholder="Your name" />
      </label>
      <label>
        <span>Email</span>
        <input required type="email" autoComplete="email" inputMode="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={254} placeholder="you@example.com" />
      </label>
      <button className="button button--primary button--wide" type="submit" disabled={status === "loading"} data-track={`waitlist_submit_${place}`}>
        {status === "loading" ? "Joining…" : buttonLabel}<ArrowRight size={18} />
      </button>
      <ErrorMessage message={error} />
      <p className="form-fineprint">Free. Launch news only, no spam. Unsubscribe in one tap.</p>
    </form>
  );
}

function PhoneDemo() {
  return (
    <div className="phone-demo" aria-label="Preview of NOKI pausing Instagram and offering a Side Quest">
      <div className="phone-demo__speaker" />
      <div className="phone-demo__screen">
        <div className="phone-demo__status"><span>9:41</span><span>● ● ●</span></div>
        <div className="paused-app">
          <div className="app-dot">◎</div>
          <span>Instagram is on hold</span>
        </div>
        <div className="quest-card quest-card--phone">
          <span className="eyebrow">SIDE QUEST · 5 MIN</span>
          <h3>Go meet the day.</h3>
          <p>Step outside. No headphones. No feed. Just notice where you are.</p>
          <div className="phone-progress"><span /></div>
        </div>
        <div className="intentional-window"><Pause size={15} /> Your phone can wait.</div>
      </div>
    </div>
  );
}

const problemCards = [
  ["01", "You don’t even decide to open Instagram anymore.", "Sometimes your hand gets there first."],
  ["02", "You already know how to ignore the limit.", "So the blocker becomes part of the routine."],
  ["03", "You don’t want your life turned into a productivity project.", "You just want more of the life you actually like."],
];

const categoryData: Array<[string, string, ReactNode]> = [
  ["Outside", "Air, light, weather, somewhere beyond the room.", <Sun />],
  ["People", "A voice, a face, a moment that can answer back.", <Users />],
  ["Create", "Make a rough little thing that did not exist before.", <Palette />],
  ["Move", "Walk, stretch, climb, shake the static loose.", <Move />],
  ["Read", "Follow a thought longer than a swipe.", <BookOpen />],
  ["Explore", "Take the turn you usually pass.", <Compass />],
];

const faqs = [
  ["Is NOKI available yet?", "Not yet. This is the pre-launch experience. We’re testing the idea with real people before we finish the native app."],
  ["What happens after I join?", "You can try your first Side Quest right away, and we send launch news. If you want more, the $9 Founding Pass unlocks the full 7-Day NOKI Reset at once."],
  ["What is the Founding Pass?", "A $9 one-time pass for the first 100 people. It gives you the 7-Day NOKI Reset immediately (seven guided real-life Side Quests, one a day), Founding Member status, priority beta access to the app and the first month of NOKI+ free. No subscription, no renewal."],
  ["Can I get a refund?", "Yes. A full refund within 14 days of purchase, and any time before beta access begins. If NOKI is cancelled before beta, every Founding Pass is refunded."],
  ["Will NOKI work on iPhone and Android?", "That is the plan. Native blocking behavior is still under development and will be tested separately."],
  ["Does the web Side Quest actually block Instagram?", "No. The pre-launch Side Quest tests the behavior and experience. Native blocking is part of the app build."],
  ["Is this another productivity app?", "No. You can use NOKI for focus, but the point is broader: more real life, less autopilot."],
  ["Does NOKI use AI?", "It may use AI behind the scenes for personalization and appropriate proof, but the product is not an AI chatbot. The goal is to get you back into real life."],
];

function HomePage() {
  const navigate = useNavigate();
  const config = usePublicConfig();
  const [modalOpen, setModalOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setStickyVisible(!entry.isIntersecting), { threshold: 0.05 });
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  function startQuest() {
    navigate("/start");
  }

  return (
    <div className="page page--home">
      <Header />
      <main>
        <section className="hero" ref={heroRef} data-section="hero">
          <div className="shell hero__grid">
            <div className="hero__copy">
              <span className="eyebrow">THE APP IS COMING · TRY IT TODAY</span>
              <h1>Trade scrolling<br />for living.</h1>
              <p className="hero__subhead">NOKI catches you on autopilot and hands you five real minutes instead of the feed. Try it now, free, right here.</p>
              <div className="hero__actions">
                <button className="button button--primary" onClick={startQuest} data-track="hero_start">Try a 5-minute Side Quest <ArrowRight size={18} /></button>
                <button className="text-button" onClick={() => { track("waitlist_modal_open", { place: "hero" }); setModalOpen(true); }} data-track="hero_waitlist">or just join the free waitlist</button>
              </div>
              <div className="truth-strip"><span><Check size={14} /> 2 taps, then 5 minutes</span><span><Check size={14} /> No download</span><span><Check size={14} /> Free</span></div>
            </div>
            <div className="hero__visual">
              <div className="sun-orbit" />
              <PhoneDemo />
              <img className="moki-hero" src="/assets/moki-fox.webp" alt="NOKI, an adventurous fox with a small backpack" width="560" height="840" fetchPriority="high" />
              <div className="hero-note"><Leaf size={16} /><span><strong>Less autopilot.</strong><br />More life.</span></div>
            </div>
          </div>
        </section>

        <section className="section problem-section" data-section="problem">
          <div className="shell">
            <span className="eyebrow">THE HUMAN MOMENT</span>
            <h2>You probably don’t need another screen-time limit.</h2>
            <div className="problem-grid">
              {problemCards.map(([number, title, copy]) => (
                <article className="problem-card" key={number}>
                  <span>{number}</span><h3>{title}</h3><p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section how-section" data-section="how_it_works">
          <div className="shell">
            <div className="section-intro section-intro--center">
              <span className="eyebrow">HOW NOKI WORKS</span>
              <h2>A tiny pause between the urge and the feed.</h2>
            </div>
            <div className="steps">
              <article className="step"><div className="step__visual"><div className="app-cluster"><span>◎</span><span>▶</span><span>𝕏</span></div><div className="pause-shield"><Pause /></div></div><div><span className="step-number">01</span><h3>Pause what pulls you in</h3><p>Choose the apps that keep stealing the moment.</p></div></article>
              <article className="step step--reverse"><div className="step__visual step__visual--quest"><img src="/assets/moki-fox.webp" alt="" loading="lazy" /><div className="mini-quest"><small>SIDE QUEST</small><strong>Go outside for 5</strong><span>Start somewhere small.</span></div></div><div><span className="step-number">02</span><h3>Do something real</h3><p>NOKI gives you a Side Quest that fits the kind of life you want more of.</p></div></article>
              <article className="step"><div className="step__visual"><div className="unlock-ring"><span>15</span><small>MIN</small></div><div className="unlock-copy">Unlocked intentionally</div></div><div><span className="step-number">03</span><h3>Come back on purpose</h3><p>Complete it, verify when needed, and unlock a short intentional window.</p></div></article>
            </div>
          </div>
        </section>

        <section className="section life-section" data-section="real_world_menu">
          <div className="shell">
            <div className="life-heading"><div><span className="eyebrow">THE REAL-WORLD MENU</span><h2>Not chores. Life.</h2></div><p>A walk. A book. A friend. A guitar. Sunlight. Cooking. A place you’ve never been. Whatever makes your day feel less automatic.</p></div>
            <div className="life-cards" role="list">
              {categoryData.map(([title, copy, icon], index) => <article role="listitem" className={`life-card life-card--${index + 1}`} key={title}><div className="life-card__icon">{icon}</div><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}
            </div>
          </div>
        </section>

        <section className="section vision-section" data-section="vision">
          <div className="shell vision-grid">
            <div className="vision-copy"><span className="eyebrow">THE VISION · COMING WITH NOKI</span><h2>The goal isn’t a lower number. It’s a fuller life.</h2><p>Over time, your Side Quests and Experiences can become memories, patterns and a Life Map — so NOKI learns what actually pulls you out of autopilot.</p><p className="coming-note"><Sparkles size={16} /> Future-product concepts, not currently available.</p></div>
            <div className="vision-phones" aria-label="Future NOKI product concepts">
              <div className="mini-phone mini-phone--memories"><small>MY LIFE</small><strong>12 moments</strong><div className="memory-grid"><span /><span /><span /><span /></div></div>
              <div className="mini-phone mini-phone--map"><small>LIFE MAP</small><Map size={58} /><div className="map-path" /></div>
              <div className="mini-phone mini-phone--compass"><small>NOKI COMPASS</small><Compass size={72} /><strong>Try somewhere new</strong></div>
            </div>
          </div>
        </section>

        <section className="human-section" data-section="rebellion">
          <img src="/assets/lifestyle-hero.webp" alt="A person stepping outside into the late-afternoon light with their phone lowered" loading="lazy" width="1120" height="1400" />
          <div className="human-section__overlay"><span className="eyebrow">A SMALL REBELLION</span><h2>AI can do more work for us than ever. It still can’t live our lives for us.</h2><p>NOKI uses technology for one job: helping you put the technology down when something better is waiting.</p></div>
        </section>

        <section className="section founder-section" data-section="founder_note">
          <div className="shell founder-grid"><div className="founder-mark">“</div><div><span className="eyebrow">A NOTE FROM THE FOUNDER</span><h2>I’m building this because I need it too.</h2><p>I’ve tried limits, blockers, deleting apps and putting the phone away. I still catch myself opening the same feeds without deciding to. NOKI is the experiment I wish already existed.</p></div></div>
        </section>

        <section className="section founding-section" id="founding" data-section="offer">
          <div className="shell">
            <div className="section-intro section-intro--center"><span className="eyebrow">FIRST COHORT</span><h2>Join before the app exists.</h2><p>Help us prove this should exist — and get into the first beta wave.</p></div>
            <div className="offer-grid">
              <article className="offer-card"><span className="offer-card__label">FREE</span><h3>Free Waitlist</h3><div className="offer-price">$0</div><ul><li><Check /> Your first Side Quest now</li><li><Check /> Launch and beta news</li><li><Check /> Free forever, no card</li></ul><button className="button button--outline button--wide" onClick={() => { track("waitlist_modal_open", { place: "offer" }); setModalOpen(true); }} data-track="offer_waitlist">Join free</button></article>
              <article className="offer-card offer-card--founding"><span className="offer-card__label">FOUNDING</span><h3>Founding Pass</h3><div className="offer-price">$9 <small>one-time{config.foundingEnabled ? "" : ", when it opens"}</small></div><ul><li><Check /> <strong>The 7-Day NOKI Reset, instant access</strong></li><li><Check /> Founding Member status</li><li><Check /> Priority beta access to the app</li><li><Check /> First month of NOKI+ free</li><li><Check /> Full refund within 14 days, or any time before beta</li></ul><Link className="button button--primary button--wide" to="/founding" data-track="offer_founding">{config.foundingEnabled ? "Become a Founding Member" : "Reserve my spot, free"}</Link><p className="real-count">{config.foundingEnabled ? `${config.foundingRemaining ?? config.foundingCap} of ${config.foundingCap} Founding Passes left` : `First cohort: ${config.foundingCap} people. No card needed to reserve.`}</p></article>
            </div>
          </div>
        </section>

        <section className="section faq-section" data-section="faq"><div className="shell faq-grid"><div><span className="eyebrow">GOOD QUESTIONS</span><h2>Before you step outside.</h2></div><div>{faqs.map(([question, answer]) => <details key={question}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}</div></div></section>

        <section className="final-cta" data-section="final_cta"><div className="shell"><img src="/assets/moki-fox.webp" alt="" loading="lazy" /><span className="eyebrow">RIGHT NOW, NOT SOMEDAY</span><h2>Give NOKI five minutes of your real life.</h2><p>You don’t need to wait for the app to try the idea.</p><button className="button button--lime" onClick={startQuest} data-track="final_start">Start my first Side Quest <ArrowRight /></button></div></section>
      </main>
      <Footer />
      {stickyVisible && <button className="sticky-cta" onClick={startQuest} data-track="sticky_start"><img src="/assets/moki-mark.svg" alt="" /> Try a Side Quest <ArrowRight size={17} /></button>}
      {modalOpen && <div className="modal" role="dialog" aria-modal="true" aria-labelledby="waitlist-title"><button className="modal__backdrop" aria-label="Close" onClick={() => setModalOpen(false)} /><div className="modal__card"><button className="icon-button modal__close" aria-label="Close" onClick={() => setModalOpen(false)}><X /></button><span className="eyebrow">FREE WAITLIST</span><h2 id="waitlist-title">Try NOKI before the app exists.</h2><p>Join, then give NOKI five minutes of your real life.</p><WaitlistForm place="modal" onSuccess={() => navigate("/start")} /></div></div>}
    </div>
  );
}

const distractions: Array<[Distraction, string]> = [["Instagram", "◎"], ["TikTok", "♪"], ["YouTube", "▶"], ["Reddit", "●"], ["X", "𝕏"], ["Other", "+"]];
const lifeOptions: Array<[LifeCategory, ReactNode]> = [["Outside", <Sun />], ["Move", <Move />], ["Read", <BookOpen />], ["Create", <Palette />], ["People", <Users />], ["Learn", <Compass />], ["Calm", <Leaf />]];
const riskOptions: RiskWindow[] = ["Morning", "Work or study", "Waiting around", "Gym", "Late night", "It's random"];

function FlowShell({ children, step, total = 2 }: { children: ReactNode; step?: number; total?: number }) {
  return <div className="flow-page"><header className="flow-header"><Brand /><Link to="/" className="icon-button" aria-label="Leave flow"><X /></Link></header>{step && <div className="progress-dots" aria-label={`Step ${step} of ${total}`}>{Array.from({ length: total }, (_, index) => <span className={index < step ? "active" : ""} key={index} />)}</div>}<main className="flow-main">{children}</main></div>;
}

function StartPage() {
  const navigate = useNavigate();
  const session = useSession();
  const config = usePublicConfig();
  // Quiz first, email second: two easy taps build the reason to leave an email ("where should NOKI
  // send your Side Quest"), instead of asking for it before anything has happened.
  const [stage, setStage] = useState<"email" | "q1" | "q2" | "q3" | "launch">(session.userId && session.quest ? "launch" : "q1");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const totalSteps = config.quizQuestion3Enabled ? 4 : 3;

  useEffect(() => { track("step_view", { step: stage === "launch" ? "quest_reveal" : stage }); }, [stage]);

  function chooseDistraction(value: Distraction) {
    updateSession({ primaryDistraction: value });
    track("quiz_q1", { choice: value });
    window.setTimeout(() => setStage("q2"), 280);
  }

  function prepareQuest(category: LifeCategory, risk?: RiskWindow) {
    const quest = assignQuest(category, risk);
    updateSession({ desiredLifeCategory: category, riskWindow: risk, quest });
    track("quiz_q2", { choice: category });
    track("quest_assigned", { quest_id: quest.id, category });
    setStage(getSession().userId ? "launch" : "email");
  }

  function chooseCategory(category: LifeCategory) {
    if (config.quizQuestion3Enabled) {
      updateSession({ desiredLifeCategory: category });
      track("quiz_q2", { choice: category });
      window.setTimeout(() => setStage("q3"), 280);
    } else {
      window.setTimeout(() => prepareQuest(category), 280);
    }
  }

  async function beginQuest() {
    const current = getSession();
    if (!current.userId || !current.quest || loading) return;
    setLoading(true);
    setError("");
    try {
      const result = await api<{ questEventId: string; startedAt: string }>("/api/quest/start", { method: "POST", body: { user_id: current.userId, quest_id: current.quest.id, category: current.quest.category } });
      updateSession({ questEventId: result.questEventId, questStartedAt: result.startedAt, questCompletedAt: undefined, feedback: undefined });
      track("quest_start", { quest_id: current.quest.id, category: current.quest.category });
      navigate("/quest");
    } catch (cause) {
      const message = cause instanceof ApiError ? cause.message : "Could not start the quest.";
      setError(message);
      track("api_error", { step: "quest_start", code: cause instanceof ApiError ? cause.code ?? String(cause.status) : "unknown", message });
      // A saved place that no longer exists on the server: ask for the email again instead of a dead end.
      if (cause instanceof ApiError && cause.code === "user_not_found") { updateSession({ userId: undefined }); setStage("email"); }
      setLoading(false);
    }
  }

  if (stage === "q1") return <FlowShell step={1} total={totalSteps}><div className="question-block"><span className="eyebrow">TWO QUICK TAPS</span><h1>What pulls you in most?</h1><div className="option-grid option-grid--apps">{distractions.map(([value, icon]) => <button onClick={() => chooseDistraction(value)} key={value} data-track={`q1_${value}`}><span>{icon}</span>{value}</button>)}</div></div></FlowShell>;
  if (stage === "q2") return <FlowShell step={2} total={totalSteps}><div className="question-block"><span className="eyebrow">NOW THE BETTER PART</span><h1>What do you want more of instead?</h1><div className="option-grid">{lifeOptions.map(([value, icon]) => <button onClick={() => chooseCategory(value)} key={value} data-track={`q2_${value}`}><span>{icon}</span>{value}</button>)}</div></div></FlowShell>;
  if (stage === "q3") return <FlowShell step={3} total={totalSteps}><div className="question-block"><span className="eyebrow">LAST ONE</span><h1>When does autopilot usually win?</h1><div className="option-list">{riskOptions.map((value) => <button key={value} onClick={() => prepareQuest(getSession().desiredLifeCategory ?? "Outside", value)}>{value}<ChevronRight /></button>)}</div></div></FlowShell>;
  if (stage === "email") return <FlowShell step={totalSteps} total={totalSteps}><div className="flow-card flow-card--center"><img className="flow-moki" src="/assets/moki-fox.webp" alt="NOKI the fox" /><span className="eyebrow">YOUR SIDE QUEST IS READY</span><h1>Where should NOKI send it?</h1><p>Your quest opens right after this. We’ll also send your invite when the app is ready.</p><WaitlistForm place="flow" onSuccess={() => setStage("launch")} buttonLabel="Show my Side Quest" /></div></FlowShell>;

  const quest = session.quest;
  if (!quest) return <Navigate to="/start" replace />;
  return <FlowShell><div className="quest-reveal"><div className="quest-reveal__top"><img src="/assets/moki-fox.webp" alt="NOKI the fox" /><span className="eyebrow">YOUR 5-MINUTE SIDE QUEST</span></div><div className="quest-card quest-card--large"><span>{quest.category}</span><h1>{quest.title}</h1><p>{quest.prompt}</p><div className="quest-card__footer"><Leaf /> Your phone can wait.</div></div><button className="button button--lime button--wide" onClick={beginQuest} disabled={loading} data-track="quest_begin">{loading ? "Starting…" : "Start 5 minutes"}<Play size={18} /></button><ErrorMessage message={error} /><button className="text-button" onClick={() => setStage("q1")} data-track="quest_change_answers"><ArrowLeft size={15} /> Change my answers</button></div></FlowShell>;
}

function QuestPage() {
  const navigate = useNavigate();
  const session = useSession();
  const startedAt = session.questStartedAt ? Date.parse(session.questStartedAt) : NaN;
  const duration = session.quest?.durationSeconds ?? 300;
  const [remaining, setRemaining] = useState(() => Number.isFinite(startedAt) ? Math.max(0, duration - Math.floor((Date.now() - startedAt) / 1000)) : duration);

  useEffect(() => {
    if (!session.quest || !Number.isFinite(startedAt)) return;
    const tick = () => setRemaining(Math.max(0, duration - Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [duration, session.quest, startedAt]);

  // The moment the five minutes are over, once per quest (a reload does not count it again).
  const doneKey = `moki-timer-done-${session.questEventId ?? ""}`;
  useEffect(() => {
    if (remaining !== 0 || !session.questEventId) return;
    try { if (localStorage.getItem(doneKey)) return; localStorage.setItem(doneKey, "1"); } catch { /* ignore */ }
    track("quest_timer_done", { quest_id: session.quest?.id ?? "", minutes_since_start: Math.round((Date.now() - startedAt) / 60000) });
  }, [remaining, doneKey, session.questEventId, session.quest?.id, startedAt]);

  if (!session.quest || !session.questEventId || !Number.isFinite(startedAt)) return <Navigate to="/start" replace />;
  const minutes = Math.floor(remaining / 60).toString().padStart(2, "0");
  const seconds = (remaining % 60).toString().padStart(2, "0");
  const progress = 1 - remaining / duration;

  function stopEarly() {
    track("quest_exit", { quest_id: session.quest?.id ?? "", seconds_left: remaining });
    navigate("/");
  }

  return <div className="timer-page"><button className="timer-exit" onClick={stopEarly} data-track="quest_stop_early">I need to stop</button><div className="timer-main"><img src="/assets/moki-fox.webp" alt="NOKI waiting calmly" /><div className="timer-ring" style={{ "--progress": `${progress * 360}deg` } as React.CSSProperties}><div><span>{minutes}:{seconds}</span><small>{remaining > 0 ? "YOUR PHONE CAN WAIT" : "YOU’RE BACK"}</small></div></div><h1>{remaining > 0 ? session.quest.title : "That was five real minutes."}</h1><p>{remaining > 0 ? "Put the phone down. NOKI will be here." : "Come back when you’re ready to close the loop."}</p>{remaining === 0 && <button className="button button--lime" onClick={() => navigate("/complete")} data-track="quest_im_back">I’m back <ArrowRight /></button>}</div></div>;
}

function CompletePage() {
  const navigate = useNavigate();
  const session = useSession();
  const config = usePublicConfig();
  const [feedback, setFeedback] = useState<QuestFeedback | undefined>(session.feedback);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [sevenDayJoined, setSevenDayJoined] = useState(false);
  const [shared, setShared] = useState(false);
  const [reasonSent, setReasonSent] = useState(false);

  if (!session.quest || !session.questEventId || !session.userId) return <Navigate to="/start" replace />;

  async function chooseFeedback(value: QuestFeedback) {
    setError("");
    try {
      const result = await api<{ completedAt: string }>("/api/quest/complete", { method: "POST", body: { user_id: session.userId, quest_event_id: session.questEventId, self_report: value } });
      updateSession({ feedback: value, questCompletedAt: result.completedAt });
      setFeedback(value);
      track("quest_complete", { quest_id: session.quest!.id });
      track("quest_feedback", { response: value });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save your answer.");
    }
  }

  async function joinSevenDay() {
    try {
      await api("/api/seven-day/join", { method: "POST", body: { user_id: session.userId } });
      setSevenDayJoined(true);
      track("seven_day_join");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not join right now.");
    }
  }

  async function share() {
    const url = `${window.location.origin}/?ref=${session.referralCode ?? ""}`;
    const data = { title: "Try NOKI", text: "Trade five minutes of scrolling for something real.", url };
    const method = "share" in navigator ? "share_sheet" : "copy_link";
    try {
      if (navigator.share) await navigator.share(data);
      else await navigator.clipboard.writeText(url);
      setShared(true);
      track("referral_share", { method });
    } catch {
      setShared(false);
      track("referral_share_cancel", { method });
    }
  }

  async function sendReason() {
    if (!reason.trim()) return;
    await api("/api/quest/reason", { method: "POST", body: { user_id: session.userId, quest_event_id: session.questEventId, reason } });
    track("reason_submit", { length: reason.trim().length });
    setReason("");
    setReasonSent(true);
  }

  if (!feedback) return <FlowShell><div className="completion-intro"><div className="completion-badge"><img src="/assets/moki-fox.webp" alt="NOKI celebrating quietly" /><Check /></div><span className="eyebrow">SIDE QUEST COMPLETE</span><h1>You just traded 5 minutes of scrolling for your actual life.</h1><p>One tap. No survey.</p><div className="feedback-buttons"><button onClick={() => void chooseFeedback("better")} data-track="feedback_better">Better than scrolling</button><button onClick={() => void chooseFeedback("same")} data-track="feedback_same">About the same</button><button onClick={() => void chooseFeedback("not_for_me")} data-track="feedback_not_for_me">Not for me</button></div><ErrorMessage message={error} /></div></FlowShell>;

  return <FlowShell><div className="after-card"><img className="after-card__moki" src="/assets/moki-fox.webp" alt="" /><span className="eyebrow">YOU MADE NOKI REAL</span><h1>{feedback === "better" ? "That feeling is the product." : feedback === "same" ? "One experiment, honestly logged." : "Thanks for being honest."}</h1><p>{feedback === "better" ? "Not less phone for the sake of a number. More of the life you actually want." : feedback === "same" ? "Try different kinds of real life over seven days and see what actually works for you." : "NOKI should earn its place. If you want, tell us what missed."}</p>
    {feedback === "not_for_me" ? <div className="reason-box"><label htmlFor="reason">What missed? <small>optional</small></label><textarea id="reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="A sentence is enough." /><button className="button button--outline" onClick={() => void sendReason()} disabled={reasonSent} data-track="reason_send">{reasonSent ? "Thank you, it helps" : "Send feedback"}</button></div> : <div className="next-grid">{false && <article><span>7 DAYS · OPTIONAL</span><h2>The Less Autopilot Experiment</h2><p>Seven different ways to trade one automatic scroll. No streak. Just a useful experiment.</p><button className="button button--outline button--wide" onClick={() => void joinSevenDay()} disabled={sevenDayJoined} data-track="seven_day_join">{sevenDayJoined ? <><Check /> You’re in</> : "Join the 7-Day experiment"}</button></article>}{<article className="next-grid__founding"><span>7 DAYS · $9 ONCE</span><h2>Want seven days of this?</h2><p>{config.foundingEnabled ? "The 7-Day NOKI Reset gives you a new real-life Side Quest every day for a week, starting now, plus a founding spot in the NOKI app." : "The Founding Pass includes the 7-Day NOKI Reset: a new real-life Side Quest every day for a week. Reserve one of the first 100 spots, free, and we email you first when it opens."}</p><button className="button button--lime button--wide" onClick={() => navigate("/founding")} data-track="complete_founding">{config.foundingEnabled ? "Get the 7-Day Reset" : "Reserve my Founding spot"}</button></article>}</div>}
    <div className="share-card"><div><span>BUILD YOUR CREW</span><h2>Know someone else living on autopilot?</h2></div><button className="button button--dark" onClick={() => void share()} data-track="share"><Share2 /> {shared ? "Link copied" : "Share NOKI"}</button></div><ErrorMessage message={error} /><button className="text-button" onClick={() => navigate("/")}>Back to NOKI</button></div></FlowShell>;
}

function FoundingPage() {
  const session = useSession();
  const config = usePublicConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [reserved, setReserved] = useState(false);

  useEffect(() => { track("founding_view", { enabled: config.foundingEnabled }); }, [config.foundingEnabled]);

  // While paid checkout is closed: record the intent to buy (a real demand signal), no card taken.
  async function reserve() {
    if (!session.userId) { track("founding_reserve_needs_signup"); navigate("/start"); return; }
    setLoading(true);
    setError("");
    const eventId = crypto.randomUUID();
    try {
      await api("/api/founding/intent", { method: "POST", body: { user_id: session.userId, event_id: eventId } });
      track("founding_intent");
      tiktokTrack("AddToWishlist", eventId, { content_name: "NOKI Founding Pass", value: 9, currency: "USD" });
      setReserved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save that. Please try again.");
      track("api_error", { step: "founding_intent", code: cause instanceof ApiError ? cause.code ?? String(cause.status) : "unknown" });
    } finally {
      setLoading(false);
    }
  }

  async function checkout() {
    if (!session.userId) { navigate("/start"); return; }
    setLoading(true);
    setError("");
    try {
      const context = await api<{ transactionId: string; clientToken: string; mode: "sandbox" | "live" }>("/api/founding/create-checkout", { method: "POST", body: { user_id: session.userId } });
      const paddle = await initializePaddle({ environment: context.mode === "sandbox" ? "sandbox" : "production", token: context.clientToken });
      if (!paddle) throw new Error("Paddle did not initialize.");
      track("founding_checkout_start");
      paddle.Checkout.open({ transactionId: context.transactionId, settings: { displayMode: "overlay", theme: "light", locale: "en" } });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Checkout is not available yet. Your waitlist place is safe.");
      setLoading(false);
    }
  }

  return <div className="page founding-page"><Header compact /><main><section className="founding-hero"><div className="shell founding-hero__grid"><div><span className="eyebrow">NOKI FOUNDING PASS</span><h1>Seven days of real life, starting today.</h1><p>Get the 7-Day NOKI Reset right away: seven guided real-life Side Quests, one a day, with a short daily email. Plus a founding spot in the NOKI app before anyone else.</p><div className="founding-price"><strong>$9</strong><span>one-time<br />{config.foundingEnabled ? "consumer purchase" : "when it opens"}</span></div>{config.foundingEnabled
            ? <button className="button button--lime" onClick={() => void checkout()} disabled={loading} data-track="founding_checkout">{loading ? "Opening checkout…" : "Become a Founding Member"}<ArrowRight /></button>
            : reserved
              ? <div className="success-note" role="status"><Check size={18} /> Your Founding spot is reserved. We’ll email you first when the pass opens.</div>
              : <button className="button button--lime" onClick={() => void reserve()} disabled={loading} data-track="founding_reserve">{loading ? "Saving…" : session.userId ? "Reserve my spot, free" : "Try a Side Quest to reserve"}<ArrowRight /></button>}
            <ErrorMessage message={error} /><p className="checkout-truth">{config.foundingEnabled ? "No subscription. No investment or ownership." : "No card, no payment now. Reserving only puts you first in line for one of the 100 passes."}</p></div><div className="founding-pass-visual"><div className="pass-card"><img src="/assets/moki-mark.svg" alt="" /><span>FOUNDING MEMBER</span><strong>NOKI</strong><small>COHORT 001</small></div><div className="cap-note"><strong>{config.foundingCap}</strong><span>people<br />in cohort one</span></div></div></div></section><section className="section"><div className="shell benefit-grid"><article><span>01</span><h2>The 7-Day NOKI Reset, now</h2><p>Seven guided real-life Side Quests, one a day: outside, people, moving, making, reading, quiet, and your own pick. Unlocked the moment you pay, with a short email each day.</p></article><article><span>02</span><h2>Founding Member status</h2><p>A permanent founding number, one of the first 100.</p></article><article><span>03</span><h2>Priority beta access</h2><p>You get into the NOKI app before anyone else, and help shape it.</p></article><article><span>04</span><h2>First month of NOKI+ free</h2><p>Applied when paid subscriptions launch.</p></article></div></section><section className="refund-section"><div className="shell"><MessageCircle /><div><span className="eyebrow">PLAIN-LANGUAGE REFUND RULE</span><h2>Early should not mean risky.</h2><p>Full refund within 14 days of purchase, and any time before beta access begins. If NOKI is cancelled before beta, every Founding Pass is refunded. No launch date is promised yet.</p></div></div></section><section className="founding-final"><div className="shell"><h2>First, try the behavior.</h2><p>NOKI should earn your belief before it asks for a purchase.</p><Link className="button button--primary" to="/start" data-track="founding_try_quest">Try your first Side Quest</Link></div></section></main><Footer /></div>;
}


interface ProgramState { active: boolean; memberNumber?: number | null; suggestedDay?: number; done?: number[] }

/** /program: the 7-Day NOKI Reset. Buyers land here from checkout and from every daily email. */
function ProgramPage() {
  const session = useSession();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get("u") || session.userId || "";
  const welcome = params.get("welcome") === "1";
  const [state, setState] = useState<ProgramState | null>(null);
  const [day, setDay] = useState<number>(Number(params.get("day")) || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) { setState({ active: false }); return; }
    let tries = 0;
    let stop = false;
    // Right after checkout, Paddle's confirmation can take a few seconds to reach us.
    const load = async () => {
      try {
        const next = await api<ProgramState>(`/api/program?u=${encodeURIComponent(userId)}`);
        if (stop) return;
        setState(next);
        if (next.active) { setDay((d) => d || next.suggestedDay || 1); track("program_view", { day: next.suggestedDay ?? 1, welcome }); return; }
      } catch { if (!stop) setState({ active: false }); }
      if (welcome && ++tries < 12 && !stop) window.setTimeout(load, 3000);
    };
    void load();
    return () => { stop = true; };
  }, [userId, welcome]);

  async function markDone(value: "better" | "same" | "not_for_me") {
    if (!state?.active || saving) return;
    setSaving(true);
    setError("");
    try {
      await api("/api/program/done", { method: "POST", body: { user_id: userId, day, self_report: value } });
      track("program_day_done", { day, response: value });
      setState({ ...state, done: [...new Set([...(state.done ?? []), day])] });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save that. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!state) return <FlowShell><div className="flow-card flow-card--center"><p>Loading your Reset…</p></div></FlowShell>;

  if (!state.active) {
    return <div className="page founding-page"><Header compact /><main><section className="founding-hero"><div className="shell">
      <span className="eyebrow">THE 7-DAY NOKI RESET</span>
      <h1>{welcome ? "Confirming your payment…" : "Seven days of real life."}</h1>
      <p>{welcome ? "This usually takes a few seconds. If this page does not update, check your email: your Day 1 link is on its way." : "One guided real-life Side Quest a day, for seven days. Included in the $9 NOKI Founding Pass."}</p>
      <div className="program-days">{PROGRAM.map((d) => <article key={d.day} className="program-day program-day--locked"><span>DAY {d.day} · {d.theme.toUpperCase()}</span><h2>{d.title}</h2><p>{d.minutes} minutes</p></article>)}</div>
      {!welcome && <Link className="button button--lime" to="/founding" data-track="program_get_pass">Get the Founding Pass <ArrowRight /></Link>}
    </div></section></main><Footer /></div>;
  }

  const current = PROGRAM.find((d) => d.day === day) ?? PROGRAM[0];
  const isDone = (state.done ?? []).includes(current.day);
  return <div className="page founding-page"><Header compact /><main><section className="founding-hero"><div className="shell">
    {welcome && <div className="success-note" role="status"><Check size={18} /> Payment confirmed. Welcome, Founding Member{state.memberNumber ? ` #${state.memberNumber}` : ""}.</div>}
    <span className="eyebrow">THE 7-DAY NOKI RESET · DAY {current.day} OF 7</span>
    <div className="program-tabs" role="tablist">{PROGRAM.map((d) => <button key={d.day} role="tab" aria-selected={d.day === current.day} className={`program-tab ${d.day === current.day ? "is-active" : ""} ${(state.done ?? []).includes(d.day) ? "is-done" : ""}`} onClick={() => { setDay(d.day); track("program_day_open", { day: d.day }); }} data-track={`program_tab_${d.day}`}>{(state.done ?? []).includes(d.day) ? <Check size={14} /> : d.day}</button>)}</div>
    <div className="quest-card quest-card--large"><span>{current.theme} · {current.minutes} MIN</span><h1>{current.title}</h1><p>{current.quest}</p><div className="quest-card__footer"><Leaf /> {current.why}</div></div>
    <p className="program-notice"><strong>One thing to notice:</strong> {current.notice}</p>
    {isDone
      ? <div className="success-note"><Check size={18} /> Day {current.day} done. {current.day < 7 ? "The next quest is ready when you are." : "That was your week. Thank you for trying NOKI this way."}</div>
      : <div className="feedback-buttons"><button onClick={() => void markDone("better")} disabled={saving} data-track="program_done_better">Done: better than scrolling</button><button onClick={() => void markDone("same")} disabled={saving} data-track="program_done_same">Done: about the same</button><button onClick={() => void markDone("not_for_me")} disabled={saving} data-track="program_done_not_for_me">Not for me today</button></div>}
    <ErrorMessage message={error} />
  </div></section></main><Footer /></div>;
}

const COMPANY = "TIGERBESTBRANDS LLC";
const COMPANY_ADDRESS = "5830 East 2nd St. PMB 90171, Casper, WY 82609, United States";

function LegalPage({ type }: { type: "privacy" | "terms" | "refund" }) {
  const config = usePublicConfig();
  const mail = <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>;
  const title = type === "privacy" ? "Privacy Policy" : type === "refund" ? "Refund Policy" : "Terms of Service";
  return <div className="page legal-page"><Header compact /><main className="shell legal-content"><Link className="back-link" to="/"><ArrowLeft /> Back to NOKI</Link><span className="eyebrow">NOKI · LEGAL</span><h1>{title}</h1><p className="legal-date">Last updated: September 29, 2026</p>
    {type === "terms" && <>
      <h2>1. Who we are</h2><p>NOKI (“we”, “us”) is a product of {COMPANY}, a company registered in the State of Wyoming, United States, at {COMPANY_ADDRESS}. These Terms govern your use of the website getnoki.app, the web Side Quest, the waitlist and the NOKI Founding Pass. By using them you agree to these Terms.</p>
      <h2>2. What NOKI is today</h2><p>NOKI is an app in development that helps people trade automatic scrolling for small real-life moments. Today you can use the free web Side Quest: a short quiz, a five-minute real-life activity and a timer. The web version does not block other apps, monitor you or verify that an activity happened. Features described as coming (for example native app pausing, Life Map and Compass) are not available yet, and no launch date is promised.</p>
      <h2>3. Eligibility</h2><p>You must be at least 13 years old to use NOKI. If you are under 18, a parent or legal guardian must agree to these Terms on your behalf.</p>
      <h2>4. The Founding Pass</h2><p>The Founding Pass is a one-time purchase of <strong>$9 USD</strong> (plus any applicable tax), limited to the first 100 members. It is not a subscription and does not renew. It includes immediate access to the 7-Day NOKI Reset (seven guided real-life Side Quests, one a day, on this website and by email, available as soon as payment is confirmed), Founding Member status, priority access to the first beta of the NOKI app, early feedback access, and the first month of NOKI+ free if and when paid subscriptions launch. It is a consumer purchase, not an investment, and gives no equity, ownership or financial return. Before paid checkout opens, you can reserve a spot for free; reserving does not create any payment obligation.</p>
      <h2>5. Payments</h2><p>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns. We never see or store your full card details. By purchasing you also agree to <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">Paddle's Buyer Terms</a>.</p>
      <h2>6. Refunds</h2><p>You can get a full refund within 14 days of purchase, and at any time before beta access begins. If NOKI is cancelled before beta, every Founding Pass is refunded automatically. Details are in our <Link to="/refund">Refund Policy</Link>.</p>
      <h2>7. Acceptable use</h2><p>Do not abuse forms, automate signups, interfere with the site, submit unlawful content, try to get around the Founding cap, or replay payment notifications. We may remove fraudulent or abusive entries.</p>
      <h2>8. No medical advice</h2><p>NOKI is not a medical or mental-health service and does not diagnose or treat any condition. If you are struggling, please contact a qualified professional.</p>
      <h2>9. Liability</h2><p>The site and the web Side Quest are provided “as is”. To the extent the law allows, {COMPANY} is not liable for indirect or consequential losses, and our total liability for any claim is limited to the amount you paid us in the 12 months before it. Nothing here limits rights you have as a consumer under the law of your country.</p>
      <h2>10. Changes and contact</h2><p>We may update these Terms as NOKI develops; the date at the top shows the latest version. Questions: {mail}, or write to {COMPANY}, {COMPANY_ADDRESS}.</p>
    </>}
    {type === "refund" && <>
      <h2>Our promise</h2><p>Early should not mean risky. Every NOKI Founding Pass comes with a full refund, no questions asked, <strong>within 14 days of purchase</strong>, and <strong>at any time before beta access begins</strong>, whichever is later. If NOKI is cancelled before beta, every Founding Pass is refunded automatically.</p>
      <h2>Payments processed by Paddle</h2><p>NOKI uses Paddle as its Merchant of Record. All payments are processed by Paddle, and refunds are issued by Paddle on our behalf to your original payment method.</p>
      <h2>EU, EEA and UK customers</h2><p>If you live in the EU, EEA or UK you also have a statutory right to withdraw from your purchase within 14 days, as described in Paddle's Buyer Terms.</p>
      <h2>How to request a refund</h2><p>Email {mail} with the subject “Refund” and the email address you used at checkout (or your Paddle receipt number). We reply within 1–2 business days and process the refund through Paddle. You can also contact Paddle directly at <a href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a>. Refunds usually appear within 5–10 business days, depending on your bank.</p>
      <h2>Contact</h2><p>{COMPANY}, {COMPANY_ADDRESS}. {mail}</p>
    </>}
    {type === "privacy" && <>
      <h2>Who is responsible</h2><p>NOKI is operated by {COMPANY}, {COMPANY_ADDRESS}. Contact for privacy questions: {mail}.</p>
      <h2>What we collect</h2><p>The email address you submit, an optional first name, your quiz answers, your Side Quest status and one-tap feedback, referral relationships, and how you reached the site (for example the campaign tags on a link). We also record anonymous product events on this site (pages viewed, buttons tapped, how far you scroll, errors and page speed) so we can see which steps work. If you buy a Founding Pass, Paddle processes your payment details and sends us the transaction status; we never receive your full card number.</p>
      <h2>What we do not collect</h2><p>No photos, microphone recordings or location data from the web Side Quest, and we do not intentionally store raw IP addresses or browser fingerprints in our analytics.</p>
      <h2>How we use it</h2><p>To run the waitlist and the Side Quest, send the emails you asked for (your quest, launch news, the 7-day experiment), understand whether NOKI is working, prevent abuse, credit referrals, and handle Founding Pass purchases and refunds.</p>
      <h2>Advertising</h2><p>If we advertise NOKI on TikTok, the site may use TikTok's pixel to measure which ads lead to a signup. It is only switched on when such campaigns run.</p>
      <h2>Service providers</h2><p>Cloudflare (hosting and database), Paddle (checkout, Merchant of Record), Resend (email delivery), and TikTok (ad measurement, only when active). Each receives only what it needs for its job.</p>
      <h2>Your choices</h2><p>Unsubscribe from any email with one tap. To access, correct or delete your data, email {mail}. Some purchase records are kept where tax or accounting law requires.</p>
      <h2>Children</h2><p>NOKI is not directed at children under 13, and we do not knowingly collect their data.</p>
    </>}
  </main><Footer /></div>;
}

function NotFoundPage() {
  return <FlowShell><div className="flow-card flow-card--center"><img className="flow-moki" src="/assets/moki-fox.webp" alt="NOKI the fox" /><span className="eyebrow">WRONG TURN</span><h1>This trail doesn’t exist.</h1><Link className="button button--primary" to="/">Back to NOKI</Link></div></FlowShell>;
}

export default function App() {
  const location = useLocation();
  useEffect(() => { startTracking(); }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Any page can be the landing page (a TikTok link may point at /founding): keep where they came from.
    captureAttribution();
    trackPageView(location.pathname);
  }, [location.pathname]);
  return <Routes><Route path="/" element={<HomePage />} /><Route path="/start" element={<StartPage />} /><Route path="/quest" element={<QuestPage />} /><Route path="/complete" element={<CompletePage />} /><Route path="/founding" element={<FoundingPage />} /><Route path="/program" element={<ProgramPage />} /><Route path="/privacy" element={<LegalPage type="privacy" />} /><Route path="/terms" element={<LegalPage type="terms" />} /><Route path="/refund" element={<LegalPage type="refund" />} /><Route path="*" element={<NotFoundPage />} /></Routes>;
}
